import React from 'react';

import classNames from 'classnames';

import { FxRate, withIncomplete } from '../../ShowInSelect';

import { columnDefinitions, HeaderCell, MoneyCell, RowLabel, Toggle, totalSuffix } from './byProjectFormat';

/** A category, department or cost centre row across the projects, and its children when open. */
function PivotRows({ rows, level, projects, locales, expanded, onToggle }) {
    return rows.map((row) => {
        const expandable = row.children.length > 0;
        const open = expandable && expanded.has(row.key);

        return (
            <React.Fragment key={row.key}>
                <tr className={level === 1 ? 'tw-bg-charcoal-10' : 'tw-text-charcoal-75'}>
                    <td className="tw-text-start">
                        <RowLabel
                            level={level}
                            className={classNames({ 'tw-font-medium': level === 1 })}
                            toggle={expandable ? <Toggle open={open} label={row.name} onClick={() => onToggle(row.key)} /> : null}
                        >
                            {row.name}
                        </RowLabel>
                    </td>
                    {projects.map((project) => (
                        <MoneyCell key={project.id} value={row.values[project.id]} locale={locales[project.id]} />
                    ))}
                    <MoneyCell value={row.total} locale={locales.total} />
                </tr>
                {open && <PivotRows rows={row.children} level={level + 1} projects={projects} locales={locales} expanded={expanded} onToggle={onToggle} />}
            </React.Fragment>
        );
    });
}

function TotalRow({ label, line, projects, locales, semantic = false }) {
    return (
        <tr>
            <td className="tw-text-start tw-font-semibold">{label}</td>
            {projects.map((project) => (
                <MoneyCell key={project.id} value={line.values[project.id]} locale={locales[project.id]} semantic={semantic} className="tw-font-semibold" />
            ))}
            <MoneyCell value={line.total} locale={locales.total} semantic={semantic} className="tw-font-semibold" />
        </tr>
    );
}

function SectionHeading({ label, span }) {
    return (
        <tr>
            <td className="tw-text-start tw-font-semibold">{label}</td>
            <td colSpan={span} />
        </tr>
    );
}

/**
 * Projects as columns: revenue categories and expenditure departments as
 * rows matched by name across projects, a dash where a project has none.
 */
export default function ProjectColumnsTable({ report, fx, committedActuals, expanded, onToggle }) {
    const pivot = report.pivot;
    const definitions = columnDefinitions(fx);
    const projects = pivot.projects;

    const locales = {
        total: report.mixed_currency ? null : report.projects.find((project) => project.locale && !project.fx?.missing)?.locale || null,
    };
    report.projects.forEach((project) => {
        locales[project.id] = project.locale;
    });

    return (
        <div className="tw-space-y-2">
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <HeaderCell definition={definitions.category} numeric={false} />
                            {projects.map((project) => (
                                <th key={project.id} className="!tw-text-end">
                                    {project.name}{' '}
                                    {project.currency && <span className="tw-text-xs tw-font-normal tw-text-charcoal-50">{project.currency}</span>}
                                    {fx && (
                                        <div className="tw-text-xs tw-font-normal">
                                            <FxRate fx={project.fx} />
                                        </div>
                                    )}
                                </th>
                            ))}
                            <HeaderCell definition={definitions.total} />
                        </tr>
                    </thead>
                    <tbody>
                        <SectionHeading label={$t('Revenue')} span={projects.length + 1} />
                        <PivotRows rows={pivot.revenue} level={1} projects={projects} locales={locales} expanded={expanded} onToggle={onToggle} />
                        <TotalRow label={$t('Total revenue')} line={pivot.total_revenue} projects={projects} locales={locales} />
                        <SectionHeading label={$t('Expenditure')} span={projects.length + 1} />
                        <PivotRows rows={pivot.expenditure} level={1} projects={projects} locales={locales} expanded={expanded} onToggle={onToggle} />
                        <TotalRow label={$t('Total expenditure')} line={pivot.total_expenditure} projects={projects} locales={locales} />
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="tw-text-start">
                                {withIncomplete(
                                    <>
                                        {$t('Net')} <span className="tw-font-normal">{totalSuffix(report)}</span>
                                    </>,
                                    fx,
                                )}
                            </td>
                            {projects.map((project) => (
                                <MoneyCell key={project.id} value={pivot.net.values[project.id]} locale={locales[project.id]} footer />
                            ))}
                            <MoneyCell value={pivot.net.total} locale={locales.total} footer />
                        </tr>
                    </tfoot>
                </table>
            </div>
            {committedActuals && (
                <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">
                    {$t('Committed and actuals are shown in the projects-as-rows layout and in the export.')}
                </p>
            )}
        </div>
    );
}
